/**
 * Types for remote reports.
 */

/** Remote reports list tab (mirrors session tabs). */
export type ReportTab =
  | 'all'
  | 'tutoring'
  | 'information-meeting'
  | 'welcome-meeting'
  | 'field-event';

/** Tutoring CR status from `GET /comptes-rendus` (`a_saisir` / `saisi` / `valide`). */
export type ReportTutoringStatus = 'a_saisir' | 'saisi' | 'valide';
/** Synthesis CR status (`incomplet` / `complet`). */
export type ReportSynthesisStatus = 'incomplet' | 'complet';
/** Union of tutoring + synthesis statuses. */
export type ReportStatus = ReportTutoringStatus | ReportSynthesisStatus;
/** Scoring completeness (`incomplet` / `complet`). */
export type ScoringStatus = 'incomplet' | 'complet';

/** Participant shown on a remote report row. */
export interface ReportParticipant {
  role: string;
  userId: string;
  displayName: string;
  roleLabel?: string | null;
  roleIcon?: string | null;
  roleColor?: string | null;
  cordRole?: string | null;
}

/** Animator / recruiter shown on RI / RA / field-event reports. */
export interface ReportAnimator {
  userId: string;
  displayName: string;
  roleLabel?: string | null;
  roleIcon?: string | null;
  roleColor?: string | null;
}

/** List row from `GET /comptes-rendus`. */
export interface ReportListItem {
  id: string;
  sessionId: string;
  sessionType: string;
  sessionDate: string; // YYYY-MM-DD
  sessionStartTime: string;
  sessionEndTime: string;
  sessionStatus: string;
  participants: ReportParticipant[];
  animator?: ReportAnimator;
  compteRenduStatus: ReportStatus;
  scoringStatus: ScoringStatus;
  lastModifiedAt: string; // ISO
}

/** Paginated `GET /comptes-rendus` payload. */
export interface ReportPaginatedResponse {
  data: ReportListItem[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** Per-user CR / scoring rights on a remote report. */
export interface ReportPermissions {
  compteRendu: 'none' | 'read' | 'write';
  scoring: 'none' | 'read' | 'write';
  canValidate: boolean;
}

/** One scoring row (`scoring_eleve` / light RI / RA). */
export interface ReportScoringEntry {
  id: string;
  targetUserId: string;
  targetRole: 'student' | 'tutor_participant';
  status: ScoringStatus;
  fields: Record<string, unknown>;
  updatedAt: string | null;
}

/** Audit event on a remote report. */
export interface ReportHistoryEvent {
  id: string;
  eventType:
    | 'created'
    | 'available'
    | 'compte_rendu_saved'
    | 'scoring_saved'
    | 'validated'
    | 'correction_requested';
  actorUserId: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

/** Mapped detail from `GET /comptes-rendus/:id` (English keys). */
export interface ReportDetail {
  id: string;
  session: {
    id: string;
    type: string;
    date: string;
    startTime: string;
    endTime: string;
    status: string;
    participants: ReportParticipant[];
    animator?: ReportAnimator;
    programId?: string | null;
    programName?: string | null;
  };
  permissions: ReportPermissions;
  reportView: ReportView;
  scoringView: ScoringView;
  history: ReportHistoryEvent[];
}

/** Wire shape of GET /comptes-rendus/:id (French keys from the API). */
export interface ReportDetailApi {
  id: string;
  session: ReportDetail['session'];
  permissions: ReportPermissions;
  vueCompteRendu: ReportView;
  vueScoring: ScoringView;
  history: ReportHistoryEvent[];
}

/** Compte-rendu form view (`cr_tutorat` / `synthese_ri` / `synthese_ra`). */
export interface ReportView {
  status: ReportStatus;
  schemaId: 'cr_tutorat' | 'synthese_ri' | 'synthese_ra';
  fields: Record<string, unknown>;
  updatedAt: string | null;
}

/** Scoring form view + per-target entries. */
export interface ScoringView {
  status: ScoringStatus;
  schemaId: 'scoring_eleve' | 'scoring_light_ri' | 'scoring_light_ra';
  entries: ReportScoringEntry[];
}

/** Query params for `GET /comptes-rendus`. */
export interface ReportListParams {
  page?: number;
  pageSize?: number;
  sessionType?: string;
  compteRenduStatus?: string;
  scoringStatus?: string;
  periodFrom?: string;
  periodTo?: string;
  studentUserId?: string;
  tutorUserId?: string;
  animatorUserId?: string;
  managedByIds?: string[];
  programId?: string;
  search?: string;
  sessionId?: string;
  schoolYearId?: string;
}

/** Map French API keys (`vueCompteRendu` / `vueScoring`) to {@link ReportDetail}. */
export function mapReportDetail(
  api: ReportDetailApi & Partial<Pick<ReportDetail, 'reportView' | 'scoringView'>>,
): ReportDetail {
  return {
    id: api.id,
    session: api.session,
    permissions: api.permissions,
    reportView: (api.vueCompteRendu ?? api.reportView)!,
    scoringView: (api.vueScoring ?? api.scoringView)!,
    history: api.history ?? [],
  };
}
